import {
  Briefcase,
  Building2,
  CircleAlert,
  Globe,
  Globe2,
  IdCard,
  Image,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Upload,
  User,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { onboardingService } from '@/services/onboardingService';
import { INDUSTRIES, SECTORS } from '@/utils/constants';
import {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  INPUT_BASE_CLASS,
  ORG_SIZES,
  SELECT_CONTENT_CLASS,
  SELECT_TRIGGER_CLASS,
  TEXTAREA_BASE_CLASS,
} from './constants';
import { FieldShell } from './components';
import type { FieldError, FormState } from './types';

interface StepProps {
  form: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  markTouched: (key: keyof FormState) => void;
  showError: (key: keyof FormState) => FieldError;
  fieldIsValid: (key: keyof FormState) => boolean;
  isSubmitting: boolean;
}

export function Step1({
  form,
  update,
  markTouched,
  showError,
  fieldIsValid,
  isSubmitting,
}: StepProps) {
  return (
    <div className="w-full space-y-6">
      <FieldShell
        id="orgName"
        label="Organization name"
        required
        tooltip="Use the full registered name as it appears on official documents."
        error={showError('orgName')}
        isValid={fieldIsValid('orgName')}
      >
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="orgName"
            value={form.orgName}
            onChange={(e) => update('orgName', e.target.value)}
            onBlur={() => markTouched('orgName')}
            placeholder="e.g. National Cyber Security Agency"
            className={cn(INPUT_BASE_CLASS, 'pl-10', showError('orgName') && 'border-red-400')}
            disabled={isSubmitting}
            autoComplete="organization"
          />
        </div>
      </FieldShell>

      <div className="grid gap-6 sm:grid-cols-2">
        <FieldShell
          id="sector"
          label="Sector"
          required
          tooltip="Select the sector that best represents your work."
          error={showError('sector')}
          isValid={fieldIsValid('sector')}
        >
          <Select
            value={form.sector}
            onValueChange={(v) => {
              update('sector', v);
              markTouched('sector');
            }}
            disabled={isSubmitting}
          >
            <SelectTrigger
              id="sector"
              className={cn(SELECT_TRIGGER_CLASS, showError('sector') && 'border-red-400')}
            >
              <SelectValue placeholder="Select sector" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT_CLASS}>
              {SECTORS.map((s) => (
                <SelectItem key={s.value} value={s.value} className="text-[14px]">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>

        <FieldShell
          id="organizationSize"
          label="Organization size"
          required
          tooltip="Choose the headcount range that matches your workforce."
          error={showError('organizationSize')}
          isValid={fieldIsValid('organizationSize')}
        >
          <Select
            value={form.organizationSize}
            onValueChange={(v) => {
              update('organizationSize', v as FormState['organizationSize']);
              markTouched('organizationSize');
            }}
            disabled={isSubmitting}
          >
            <SelectTrigger
              id="organizationSize"
              className={cn(SELECT_TRIGGER_CLASS, showError('organizationSize') && 'border-red-400')}
            >
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT_CLASS}>
              {ORG_SIZES.map((s) => (
                <SelectItem key={s.value} value={s.value} className="text-[14px]">
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      </div>

      <FieldShell
        id="industry"
        label="Organization Type"
        optional
        tooltip="Select the ownership or institutional type of your organization."
        error={showError('industry')}
        isValid={fieldIsValid('industry')}
      >
        <Select
          value={form.industry}
          onValueChange={(v) => {
            update('industry', v);
            markTouched('industry');
          }}
          disabled={isSubmitting}
        >
          <SelectTrigger
            id="industry"
            className={cn(SELECT_TRIGGER_CLASS, showError('industry') && 'border-red-400')}
          >
            <SelectValue placeholder="Select organization type" />
          </SelectTrigger>
          <SelectContent className={SELECT_CONTENT_CLASS}>
            {INDUSTRIES.map((i) => (
              <SelectItem key={i.value} value={i.value} className="text-[14px]">
                {i.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldShell>

        <FieldShell
          id="website"
          label="Official website"
          optional
          tooltip="Use the full URL beginning with https:// - this helps verify your institution."
          error={showError('website')}
          isValid={fieldIsValid('website')}
        >
          <div className="relative">
            <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="website"
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
              onBlur={() => markTouched('website')}
              placeholder="https://www.example.gov.et"
              className={cn(INPUT_BASE_CLASS, 'pl-10', showError('website') && 'border-red-400')}
              disabled={isSubmitting}
              autoComplete="url"
              inputMode="url"
            />
          </div>
        </FieldShell>

        <FieldShell
          id="logo"
          label="Organization logo"
          optional
          tooltip="Upload your organization's logo (PNG, JPG, or SVG, max 2MB)"
          error={showError('logo')}
        >
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              {form.logoPreview ? (
                <div className="relative h-24 w-24 overflow-hidden rounded-lg border border-border bg-muted">
                  <img
                    src={form.logoPreview}
                    alt="Organization logo preview"
                    className="h-full w-full object-contain p-2"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      update('logo', null);
                      update('logoPreview', '');
                      update('logoUrl', '');
                    }}
                    className="absolute right-1 top-1 rounded-full bg-background/80 p-1 hover:bg-background"
                    disabled={isSubmitting}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted">
                  <Image className="h-8 w-8 text-muted-foreground" />
                </div>
              )}

              <div className="flex-1">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    if (file.size > 2 * 1024 * 1024) {
                      toast.error('Logo file size must be less than 2MB');
                      return;
                    }

                    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
                    if (!validTypes.includes(file.type)) {
                      toast.error('Logo must be PNG, JPG, or SVG format');
                      return;
                    }

                    // Show an immediate local preview, then upload the file so
                    // only its URL travels in the registration payload.
                    update('logo', file);
                    update('logoPreview', URL.createObjectURL(file));

                    try {
                      const url = await onboardingService.uploadLogo(file);
                      update('logoUrl', url);
                    } catch {
                      toast.error('Failed to upload logo. Please try again.');
                      update('logo', null);
                      update('logoPreview', '');
                      update('logoUrl', '');
                    }
                  }}
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="logo-upload"
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
                    isSubmitting && 'pointer-events-none opacity-50',
                  )}
                >
                  <Upload className="h-4 w-4" />
                  {form.logo ? 'Change logo' : 'Upload logo'}
                </label>
                {form.logo && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {form.logo.name} ({(form.logo.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          </div>
        </FieldShell>
    </div>
  );
}

export function Step2Combined({
  form,
  update,
  markTouched,
  showError,
  fieldIsValid,
  userEmail,
  isSubmitting,
}: StepProps & { userEmail?: string }) {
  const len = form.description.trim().length;

  return (
    <div className="w-full space-y-8">
      {/* Location Section */}
      <div className="space-y-6">
        <h3 className="text-base font-semibold text-foreground border-b border-border/60 pb-2">
          Location
        </h3>

        <div className="grid gap-6 sm:grid-cols-2">
          <FieldShell
            id="country"
            label="Country"
            required
            tooltip="Registration is restricted to Ethiopia-based organizations."
            error={showError('country')}
            isValid={fieldIsValid('country')}
          >
            <div className="relative">
              <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="country"
                value="Ethiopia"
                readOnly
                disabled
                aria-readonly="true"
                className={cn(INPUT_BASE_CLASS, 'pl-10 cursor-not-allowed opacity-90')}
              />
            </div>
          </FieldShell>

          <FieldShell
            id="city"
            label="City"
            required
            tooltip="Enter the city where your headquarters is located."
            error={showError('city')}
            isValid={fieldIsValid('city')}
          >
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="city"
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
                onBlur={() => markTouched('city')}
                placeholder="e.g. Addis Ababa"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('city') && 'border-red-400')}
                disabled={isSubmitting}
              />
            </div>
          </FieldShell>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <FieldShell
            id="address"
            label="Address"
            required
            tooltip="Provide your organization's physical headquarters address."
            error={showError('address')}
            isValid={fieldIsValid('address')}
          >
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="address"
                value={form.address}
                onChange={(e) => update('address', e.target.value)}
                onBlur={() => markTouched('address')}
                placeholder="Building, street, district"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('address') && 'border-red-400')}
                disabled={isSubmitting}
                autoComplete="street-address"
              />
            </div>
          </FieldShell>

          <FieldShell
            id="phoneNumber"
            label="Phone"
            required
            tooltip="Enter a valid contact phone number."
            error={showError('phoneNumber')}
            isValid={fieldIsValid('phoneNumber')}
          >
            <div className="flex gap-2">
              <Input
                value="+251"
                readOnly
                disabled
                aria-readonly="true"
                aria-label="Country dial code"
                className={cn(INPUT_BASE_CLASS, 'w-[76px] cursor-not-allowed text-center opacity-90 shrink-0 font-medium')}
              />
              <div className="relative flex-1">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="phoneNumber"
                  value={form.phoneNumber}
                  onChange={(e) => update('phoneNumber', e.target.value)}
                  onBlur={() => markTouched('phoneNumber')}
                  placeholder="912345678"
                  className={cn(INPUT_BASE_CLASS, 'pl-10', showError('phoneNumber') && 'border-red-400')}
                  disabled={isSubmitting}
                  type="tel"
                  autoComplete="tel-national"
                />
              </div>
            </div>
          </FieldShell>
        </div>
      </div>

      {/* Primary Contact Section */}
      <div className="space-y-6">
        <h3 className="text-base font-semibold text-foreground border-b border-border/60 pb-2">
          Primary Contact
        </h3>

        <div className="grid gap-6 sm:grid-cols-2">
          <FieldShell
            id="contactPerson"
            label="Full name"
            required
            hint="Auto-filled from your registered account"
            tooltip="Full name of the primary organizational contact."
            error={showError('contactPerson')}
            isValid={fieldIsValid('contactPerson')}
          >
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="contactPerson"
                value={form.contactPerson}
                onChange={(e) => update('contactPerson', e.target.value)}
                onBlur={() => markTouched('contactPerson')}
                placeholder="Full name"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('contactPerson') && 'border-red-400')}
                disabled={isSubmitting}
                autoComplete="name"
              />
            </div>
          </FieldShell>

          <FieldShell
            id="contactRole"
            label="Job position"
            required
            tooltip="Official job title or position within the organization."
            error={showError('contactRole')}
            isValid={fieldIsValid('contactRole')}
          >
            <div className="relative">
              <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="contactRole"
                value={form.contactRole}
                onChange={(e) => update('contactRole', e.target.value)}
                onBlur={() => markTouched('contactRole')}
                placeholder="e.g. IT Director"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('contactRole') && 'border-red-400')}
                disabled={isSubmitting}
                autoComplete="organization-title"
              />
            </div>
          </FieldShell>
        </div>

        <FieldShell
          id="contactEmail"
          label="Email"
          required
          hint="This is your logged-in email"
          tooltip="The primary official email linked to your account."
          isValid={Boolean(userEmail)}
        >
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="contactEmail"
              value={userEmail || ''}
              className={cn(INPUT_BASE_CLASS, 'pl-10 bg-muted cursor-not-allowed opacity-90')}
              disabled
              type="email"
            />
          </div>
        </FieldShell>
      </div>

      {/* Alternate Contact Section */}
      <div className="space-y-6">
        <div className="border-b border-border/60 pb-2">
          <div className="flex items-baseline gap-2">
            <h3 className="text-base font-semibold text-foreground">Alternate Contact</h3>
            <span className="text-xs text-muted-foreground">(Optional - Add if primary contact is unavailable)</span>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <FieldShell
            id="alternateContactName"
            label="Full name"
            optional
            tooltip="Full name of an alternate contact person."
            error={showError('alternateContactName')}
            isValid={fieldIsValid('alternateContactName')}
          >
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="alternateContactName"
                value={form.alternateContactName}
                onChange={(e) => update('alternateContactName', e.target.value)}
                onBlur={() => markTouched('alternateContactName')}
                placeholder="Full name"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('alternateContactName') && 'border-red-400')}
                disabled={isSubmitting}
                autoComplete="name"
              />
            </div>
          </FieldShell>

          <FieldShell
            id="alternateContactRole"
            label="Job position"
            optional
            tooltip="Official job title of the alternate contact."
            error={showError('alternateContactRole')}
            isValid={fieldIsValid('alternateContactRole')}
          >
            <div className="relative">
              <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="alternateContactRole"
                value={form.alternateContactRole}
                onChange={(e) => update('alternateContactRole', e.target.value)}
                onBlur={() => markTouched('alternateContactRole')}
                placeholder="e.g. Deputy Director"
                className={cn(INPUT_BASE_CLASS, 'pl-10', showError('alternateContactRole') && 'border-red-400')}
                disabled={isSubmitting}
                autoComplete="organization-title"
              />
            </div>
          </FieldShell>
        </div>

        <FieldShell
          id="alternateContactEmail"
          label="Email"
          optional
          tooltip="Official email address of the alternate contact."
          error={showError('alternateContactEmail')}
          isValid={fieldIsValid('alternateContactEmail')}
        >
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="alternateContactEmail"
              value={form.alternateContactEmail}
              onChange={(e) => update('alternateContactEmail', e.target.value)}
              onBlur={() => markTouched('alternateContactEmail')}
              placeholder="alternate@example.com"
              className={cn(INPUT_BASE_CLASS, 'pl-10', showError('alternateContactEmail') && 'border-red-400')}
              disabled={isSubmitting}
              type="email"
              autoComplete="email"
            />
          </div>
        </FieldShell>
      </div>

      {/* Organization Description Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-foreground border-b border-border/60 pb-2">
          Organization Description
        </h3>
        <FieldShell
          id="description"
          label="Description"
          optional
          hint={
            len > 0
              ? `${len}/${DESCRIPTION_MAX} characters (min ${DESCRIPTION_MIN} if provided)`
              : `Optional - ${DESCRIPTION_MAX} characters max`
          }
          tooltip="Describe your organization's mission, activities, and operational scope."
          error={showError('description')}
          isValid={fieldIsValid('description')}
        >
          <textarea
            id="description"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            onBlur={() => markTouched('description')}
            placeholder="Describe your organization's mission, activities, and scope (optional)"
            className={cn(TEXTAREA_BASE_CLASS, showError('description') && 'border-red-400', 'min-h-[110px]')}
            disabled={isSubmitting}
            maxLength={DESCRIPTION_MAX}
          />
        </FieldShell>
      </div>
    </div>
  );
}

export function Step3Review({
  form,
  update,
  showError,
  isSubmitting,
  onEdit,
}: Omit<StepProps, 'markTouched' | 'fieldIsValid'> & { onEdit: (step: number) => void }) {
  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl border border-border shadow-soft p-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-0 transition-transform duration-500 group-hover:scale-110" />
        <div className="flex justify-between items-start mb-6 border-b border-border pb-4 relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <Building2 size={24} />
            </div>
            <h3 className="text-lg font-bold text-foreground">Organization Details</h3>
          </div>
          <button
            onClick={() => onEdit(1)}
            disabled={isSubmitting}
            className="text-xs font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-1.5"
          >
            <Pencil size={14} /> Edit
          </button>
        </div>
        <div className="space-y-4 relative z-10">
          {form.logoPreview && (
            <div className="mb-4">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-2">
                Logo
              </span>
              <img
                src={form.logoPreview}
                alt="Organization logo"
                className="h-16 w-16 rounded-md border border-border bg-muted object-contain p-1"
              />
            </div>
          )}
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Name</span>
            <p className="text-sm font-semibold text-foreground">{form.orgName || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Sector</span>
            <p className="text-sm font-semibold text-foreground">{form.sector}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
              Organization Type
            </span>
            <p className="text-sm font-semibold text-foreground">{form.industry || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Size</span>
            <p className="text-sm font-semibold text-foreground">{form.organizationSize}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Website</span>
            <a
              href={form.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-primary hover:underline flex items-center gap-1.5"
            >
              <Globe size={14} />
              {form.website || 'Not provided'}
            </a>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border shadow-soft p-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent-foreground/5 rounded-bl-full -z-0 transition-transform duration-500 group-hover:scale-110" />
        <div className="flex justify-between items-start mb-6 border-b border-border pb-4 relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-foreground/10 rounded-lg text-accent-foreground">
              <IdCard size={24} />
            </div>
            <h3 className="text-lg font-bold text-foreground">Contact Information</h3>
          </div>
          <button
            onClick={() => onEdit(2)}
            disabled={isSubmitting}
            className="text-xs font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-1.5"
          >
            <Pencil size={14} /> Edit
          </button>
        </div>
        <div className="space-y-4 relative z-10">
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
              Primary Contact
            </span>
            <p className="text-sm font-semibold text-foreground">{form.contactPerson || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Job Position</span>
            <p className="text-sm font-semibold text-foreground">{form.contactRole || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Phone</span>
            <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <Phone size={14} className="text-muted-foreground" />
              {form.phoneNumber ? `${form.phoneDial} ${form.phoneNumber}` : 'Not provided'}
            </p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Location</span>
            <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <MapPin size={14} className="text-muted-foreground" />
              {form.city && form.country ? `${form.city}, ${form.country}` : 'Not provided'}
            </p>
          </div>
          {(form.alternateContactName || form.alternateContactEmail) && (
            <>
              <div className="border-t border-border pt-4 mt-4">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
                  Alternate Contact
                </span>
                <p className="text-sm font-semibold text-foreground">{form.alternateContactName || 'None'}</p>
              </div>
              {form.alternateContactRole && (
                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
                    Alt. Job Position
                  </span>
                  <p className="text-sm font-semibold text-foreground">{form.alternateContactRole}</p>
                </div>
              )}
              {form.alternateContactEmail && (
                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
                    Alt. Email
                  </span>
                  <p className="text-sm font-semibold text-foreground">{form.alternateContactEmail}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bg-surface-container rounded-2xl p-6 border border-border flex items-start gap-4">
        <Checkbox
          id="acknowledgeAccuracy"
          checked={form.acknowledgeAccuracy}
          onCheckedChange={(checked) => update('acknowledgeAccuracy', Boolean(checked))}
          disabled={isSubmitting}
          className={cn(
            'mt-1 w-5 h-5 rounded text-primary focus:ring-primary border-border bg-card cursor-pointer',
            showError('acknowledgeAccuracy') && 'border-red-400',
          )}
        />
        <label htmlFor="acknowledgeAccuracy" className="text-sm text-foreground cursor-pointer select-none">
          I confirm that all information provided is accurate and represents my organization.
        </label>
      </div>
      {showError('acknowledgeAccuracy') && (
        <p className="text-sm text-red-600 flex items-center gap-2">
          <CircleAlert size={16} />
          {showError('acknowledgeAccuracy')}
        </p>
      )}
    </div>
  );
}
